import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import ChatIcon from "@mui/icons-material/ChatBubbleOutlined";
import ArchitectureIcon from "@mui/icons-material/Architecture";
import DeveloperBoardIcon from "@mui/icons-material/DeveloperBoard";
import FactCheckIcon from "@mui/icons-material/FactCheckOutlined";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import CloseIcon from "@mui/icons-material/Close";
import { Reveal } from "@/components/Reveal";
import { CtaSection } from "@/components/CtaSection";
import { PlatformArchitecture } from "@/components/PlatformArchitecture";
import { PageBanner } from "@/components/PageBanner";
import { cardBase, container, eyebrow } from "@/lib/ui";
import { products, type Category, type ProductId } from "@/lib/products";

const filters = ["all", "smartEnergy", "productRnD", "aiService"] as const;
type Filter = (typeof filters)[number];

const stepIcons = [ChatIcon, ArchitectureIcon, DeveloperBoardIcon, FactCheckIcon, AutorenewIcon];

type ProductInfo = { name: string; desc: string; tags: string[] };

export function Products() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<Filter>("all");
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);

  const catLabels = t("products:categoryLabels", { returnObjects: true }) as unknown as Record<Filter, string>;
  const catIntro = t("products:categoryIntro", { returnObjects: true }) as unknown as Record<Category, string>;
  const items = t("products:items", { returnObjects: true }) as unknown as Record<ProductId, ProductInfo>;
  const steps = t("products:process.steps", { returnObjects: true }) as unknown as {
    title: string;
    desc: string;
  }[];

  const visible = filter === "all" ? products : products.filter((p) => p.category === filter);

  return (
    <>
      {/* 2.2 Banner */}
      <PageBanner
        eyebrow={t("products:banner.eyebrow")}
        title={t("products:banner.title")}
        subtitle={t("products:banner.subtitle")}
      />

      {/* 2.3 Platform architecture */}
      <section className="bg-bg py-24 md:py-32">
        <div className={container}>
          <Reveal className="mx-auto max-w-3xl text-center">
            <p className={eyebrow}>{t("products:archSection.eyebrow")}</p>
            <h2 className="mt-4 font-display text-3xl font-bold md:text-4xl">
              {t("products:archSection.title")}
            </h2>
            <p className="mt-4 text-fg-muted">{t("products:archSection.subtitle")}</p>
          </Reveal>
          <PlatformArchitecture className="mt-14" />
        </div>
      </section>

      {/* 2.4 + 2.5 Categories + product grid */}
      <section className="bg-surface py-24 md:py-32">
        <div className={container}>
          <Reveal>
            <p className={eyebrow}>{t("products:catSection.eyebrow")}</p>
            <h2 className="mt-4 font-display text-3xl font-bold md:text-4xl">
              {t("products:catSection.title")}
            </h2>
          </Reveal>

          <div className="mt-10 flex flex-wrap gap-2">
            {filters.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={`rounded-full px-5 py-2.5 text-sm font-medium transition-all duration-300 ${
                  filter === f
                    ? "bg-gradient-to-br from-brand-deep via-brand to-brand-cyan text-white shadow-[0_8px_24px_-8px_rgba(1,120,254,0.65)]"
                    : "border border-hairline text-fg-muted hover:border-brand/40 hover:text-fg"
                }`}
              >
                {catLabels[f]}
              </button>
            ))}
          </div>

          {filter !== "all" && (
            <div className="mt-6 flex max-w-2xl gap-3 rounded-xl border border-hairline bg-elevated/40 p-4">
              <span className="w-1 shrink-0 rounded-full bg-gradient-to-b from-brand to-brand-cyan" />
              <p className="text-sm leading-relaxed text-fg-muted">{catIntro[filter]}</p>
            </div>
          )}

          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence mode="popLayout">
              {visible.map((p) => {
                const info = items[p.id];
                return (
                  <motion.article
                    key={p.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className={`group relative flex h-full flex-col overflow-hidden ${cardBase} hover:-translate-y-1.5 hover:border-brand/40 hover:shadow-[0_24px_60px_-24px_rgba(1,120,254,0.45)]`}
                  >
                    <span className="pointer-events-none absolute inset-x-0 top-0 z-10 h-0.5 origin-left scale-x-0 bg-gradient-to-r from-brand to-brand-cyan transition-transform duration-300 group-hover:scale-x-100" />
                    <button
                      type="button"
                      onClick={() => setLightbox({ src: p.image, alt: info.name })}
                      aria-label={t("lightbox.zoom", { name: info.name })}
                      className={`relative block aspect-[16/10] w-full cursor-zoom-in overflow-hidden ${
                        p.fit === "contain" ? "bg-bg p-3" : ""
                      }`}
                    >
                      <img
                        src={p.image}
                        alt={info.name}
                        className={`h-full w-full transition-transform duration-500 group-hover:scale-105 ${
                          p.fit === "contain" ? "object-contain" : "object-cover"
                        }`}
                      />
                      <span className="absolute left-3 top-3 rounded-full bg-black/55 px-3 py-1 text-xs font-medium text-white backdrop-blur">
                        {catLabels[p.category]}
                      </span>
                      <span className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all duration-300 group-hover:bg-black/30 group-hover:opacity-100">
                        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/90 text-[#0b0f14] shadow-lg">
                          <ZoomInIcon style={{ fontSize: 24 }} />
                        </span>
                      </span>
                    </button>
                    <div className="flex flex-1 flex-col p-6">
                      <h3 className="text-lg font-semibold">{info.name}</h3>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {info.tags.map((tag) => (
                          <span
                            key={tag}
                            className="rounded-full border border-hairline px-2.5 py-0.5 text-xs text-fg-muted"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      <p className="mt-4 text-sm leading-relaxed text-fg-muted">{info.desc}</p>
                    </div>
                  </motion.article>
                );
              })}
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* 2.7 Cooperation process */}
      <section className="bg-bg py-24 md:py-32">
        <div className={container}>
          <Reveal className="mx-auto max-w-2xl text-center">
            <p className={eyebrow}>{t("products:process.eyebrow")}</p>
            <h2 className="mt-4 font-display text-3xl font-bold md:text-4xl">
              {t("products:process.title")}
            </h2>
          </Reveal>

          <div className="relative mt-16">
            {/* desktop: horizontal connector; mobile: vertical timeline spine */}
            <div className="absolute left-0 right-0 top-7 hidden h-px bg-gradient-to-r from-transparent via-brand/40 to-transparent lg:block" />
            <div className="absolute bottom-6 left-7 top-6 w-px bg-gradient-to-b from-transparent via-brand/40 to-transparent lg:hidden" />
            <div className="grid gap-9 lg:grid-cols-5 lg:gap-8">
              {steps.map((step, i) => {
                const Icon = stepIcons[i]!;
                return (
                  <Reveal
                    key={step.title}
                    delay={i * 0.1}
                    className="relative flex items-start gap-5 text-left lg:block lg:text-center"
                  >
                    <div className="relative z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-elevated text-brand ring-1 ring-inset ring-brand/25 shadow-[0_10px_30px_-10px_rgba(1,120,254,0.5)] lg:mx-auto">
                      <Icon style={{ fontSize: 26 }} />
                    </div>
                    <div className="pt-2.5 lg:pt-0">
                      <h3 className="font-semibold lg:mt-5">{step.title}</h3>
                      <p className="mt-1.5 text-sm text-fg-muted lg:mt-2">{step.desc}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <CtaSection variant="products" />

      {/* Product image lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm md:p-10"
          >
            <button
              type="button"
              onClick={() => setLightbox(null)}
              aria-label={t("lightbox.close")}
              className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white backdrop-blur transition-colors hover:bg-white/20"
            >
              <CloseIcon />
            </button>
            <motion.figure
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.94, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 28 }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-full max-w-5xl"
            >
              <img
                src={lightbox.src}
                alt={lightbox.alt}
                className="max-h-[80vh] w-auto rounded-xl object-contain shadow-2xl"
              />
              <figcaption className="mt-3 text-center text-sm text-white/80">
                {lightbox.alt}
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
